import type { WellnessProgramDto } from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-294 (J12) — per-patient versioned Wellness Program client for the admin
// patient page. Auth rides the httpOnly clinician session cookie
// (`withCredentials`), scoped by `tenantHeader()`, like flows-api.ts. This
// surface is read-mostly + activate/archive; the full editing UI is J8/J9.

export class WellnessProgramApiError extends Error {}

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new WellnessProgramApiError(detail);
  }
  return (await res.json()) as T;
}

const base = (patientId: string) =>
  `${API_BASE}/employee-api/v1/patients/${patientId}/wellness-programs`;

export async function listWellnessPrograms(
  patientId: string,
): Promise<WellnessProgramDto[]> {
  const res = await apiFetch(base(patientId), {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function activateWellnessProgram(
  patientId: string,
  programId: string,
): Promise<WellnessProgramDto> {
  const res = await apiFetch(`${base(patientId)}/${programId}/activate`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function archiveWellnessProgram(
  patientId: string,
  programId: string,
): Promise<WellnessProgramDto> {
  const res = await apiFetch(`${base(patientId)}/${programId}/archive`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}
